import dayjs from "dayjs";
import type {
  WhatsAppApiButton,
  WhatsAppApiTemplate,
  WhatsAppMediaType,
  WhatsAppVariableMapping,
} from "src/services/WhatsAppCampaignAPI";
import { DATETIME_VALUE_FORMAT, TEMPLATE_PLACEHOLDER_PATTERN } from "./WhatsAppCampaignConstants";
import type {
  WhatsAppTemplate,
  WhatsAppTemplateButton,
  WhatsAppTemplateStatus,
  WhatsAppVariableDraft,
  WhatsAppVariableDrafts,
} from "./WhatsAppCampaignTypes";

const BYTES_PER_KB = 1024;
const BUTTON_KEY_PREFIX = "button_";
const MEDIA_TYPES: WhatsAppMediaType[] = ["image", "video", "document"];
const DIRECT_STATUSES: WhatsAppTemplateStatus[] = ["approved", "pending", "rejected"];
const HTTP_PROTOCOLS = ["http:", "https:"];
const DYNAMIC_URL_SUFFIX_PATTERN = /\{\{\d+\}\}.*$/;

export type TemplateBodySegment =
  | { type: "text"; value: string }
  | { type: "parameter"; index: number };

export const splitTemplateBody = (body: string): TemplateBodySegment[] =>
  body
    .split(TEMPLATE_PLACEHOLDER_PATTERN)
    .map((part, position): TemplateBodySegment =>
      position % 2 ? { type: "parameter", index: Number(part) } : { type: "text", value: part }
    )
    .filter(segment => segment.type === "parameter" || segment.value);

export const formatTemplateName = (name: string): string =>
  name
    .split("_")
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

export const toHttpUrl = (value?: string | null): string | null => {
  try {
    return value && HTTP_PROTOCOLS.includes(new URL(value).protocol) ? value : null;
  } catch {
    return null;
  }
};

const toStatus = (displayStatus?: string | null): WhatsAppTemplateStatus =>
  DIRECT_STATUSES.find(status => status === displayStatus) ?? "unavailable";

const toMediaType = (value?: string | null): WhatsAppMediaType | null =>
  MEDIA_TYPES.find(type => type === value?.toLowerCase()) ?? null;

const toButtons = (apiButtons: WhatsAppApiButton[]): WhatsAppTemplateButton[] => {
  let dynamicUrlCount = 0;
  return apiButtons.map(button => {
    const isDynamicUrl = button.type === "URL" && Boolean(button.dynamic);
    dynamicUrlCount += isDynamicUrl ? 1 : 0;
    return {
      index: button.index,
      type: button.type,
      kind: button.kind ?? "quick_reply",
      text: button.text ?? "",
      url: button.url ?? null,
      variableKey: isDynamicUrl ? `${BUTTON_KEY_PREFIX}${dynamicUrlCount}` : null,
    };
  });
};

export const toWhatsAppTemplate = (
  apiTemplate: WhatsAppApiTemplate,
  defaultMetaUrl: string | null
): WhatsAppTemplate | null => {
  if (!apiTemplate?.id || !apiTemplate.name) {
    return null;
  }
  const summary = apiTemplate.summary;
  const status = toStatus(apiTemplate.display_status);
  return {
    id: apiTemplate.id,
    name: apiTemplate.name,
    language: apiTemplate.language ?? "",
    status,
    body: summary?.body_text ?? "",
    headerFormat: summary?.header_format ?? null,
    headerText: summary?.header_text ?? null,
    mediaType: toMediaType(summary?.media_attachment),
    variableKeys: summary?.variable_keys ?? [],
    buttons: toButtons(summary?.buttons ?? []),
    metaUrl: toHttpUrl(apiTemplate.meta_manage_url) ?? defaultMetaUrl,
    usedInCampaign: apiTemplate.used_in_campaign?.campaign_name ?? null,
    rejectionReason: apiTemplate.rejected_reason ?? null,
    canUse: status === "approved" && apiTemplate.can_use === true,
    unsupportedReasons: apiTemplate.unsupported_reasons ?? [],
  };
};

export const isButtonKey = (key: string): boolean => key.startsWith(BUTTON_KEY_PREFIX);

export const getVariableRowKeys = (template: WhatsAppTemplate): string[] =>
  template.variableKeys.filter(key => !isButtonKey(key));

export const getBodyVariableCount = (template: WhatsAppTemplate): number =>
  template.variableKeys.filter(key => key.startsWith("body_")).length;

export const getVariablePlaceholder = (key: string): string => `{{${key.split("_")[1] ?? ""}}}`;

export const getButtonFixedUrl = (url: string | null): string =>
  (url ?? "").replace(DYNAMIC_URL_SUFFIX_PATTERN, "");

export const createEmptyDraft = (): WhatsAppVariableDraft => ({
  type: "static",
  value: "",
  fallback: "",
});

export const getInitialDrafts = (template: WhatsAppTemplate): WhatsAppVariableDrafts =>
  Object.fromEntries(template.variableKeys.map(key => [key, createEmptyDraft()]));

export const buildVariableMappings = (
  template: WhatsAppTemplate,
  drafts: WhatsAppVariableDrafts,
  timezone: string
): WhatsAppVariableMapping[] =>
  template.variableKeys.flatMap((key): WhatsAppVariableMapping[] => {
    const draft = drafts[key];
    const value = draft?.value.trim();
    if (!draft || !value) {
      return [];
    }
    const fallback = draft.fallback.trim();
    if (draft.type === "column") {
      return [{ key, type: "column", value, ...(fallback && { fallback }) }];
    }
    return draft.type === "date"
      ? [{ key, type: "date", value, timezone }]
      : [{ key, type: "static", value }];
  });

export const hasAllVariableValues = (
  template: WhatsAppTemplate,
  drafts: WhatsAppVariableDrafts
): boolean => template.variableKeys.every(key => Boolean(drafts[key]?.value.trim()));

export const formatDraftDate = (date: dayjs.Dayjs): string => date.format(DATETIME_VALUE_FORMAT);

export const formatFileSizeKb = (bytes: number): number => Math.round(bytes / BYTES_PER_KB);

export const readImageDimensions = (url: string): Promise<{ width: number; height: number }> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
    image.onerror = reject;
    image.src = url;
  });
